import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Space,
  Button,
  Breadcrumb,
  Typography,
  Row,
  Col,
  Input,
  Form,
  DatePicker,
  Empty,
  Tooltip,
  message,
  Modal,
  Timeline,
  Select,
  Divider,
} from "antd";
import {
  SearchOutlined,
  ArrowLeftOutlined,
  FileTextOutlined,
  UserOutlined,
  CalendarOutlined,
  EyeOutlined,
  PhoneOutlined,
  InfoCircleOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  RedoOutlined,
  HourglassOutlined,
  HomeOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  SendOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import formatDateTime, { formatDateTimeCustomize } from "../../../utils/dateTime";
import dayjs from "dayjs";
import { rewardAPIWithErrorHandling } from "../../../api/reward/rewardApi";
import ImportExportMenu from "../../../components/cms/ImportExportMenu";
import { brand, semantic, neutral } from "../../../theme/tokens";

const { RangePicker } = DatePicker;
const { Text, Paragraph } = Typography;

const DEFAULT_PAGE_SIZE = 10;

/* ─── status config ──────────────────────────────────────────── */
const STATUS_CONFIG = {
  sent:    { color: "success",   label: "Hoàn thành",    tagColor: "#389e0d", bg: "#f6ffed", border: "#b7eb8f", icon: <CheckCircleOutlined /> },
  queued:  { color: "processing",label: "Đang xử lý",    tagColor: brand.accent, bg: "#e6f4ff", border: "#91caff", icon: <HourglassOutlined /> },
  failed:  { color: "error",     label: "Thất bại",      tagColor: "#cf1322", bg: "#fff1f0", border: "#ffa39e", icon: <WarningOutlined /> },
  pending: { color: "warning",   label: "Chưa xử lý",   tagColor: "#d46b08", bg: "#fff7e6", border: "#ffd591", icon: <ClockCircleOutlined /> },
};

function StatusPill({ status }) {
  const cfg = STATUS_CONFIG[status];
  if (!cfg) return <Text type="secondary" style={{ fontSize: 12 }}>{status || "—"}</Text>;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      fontSize: 12, fontWeight: 500,
      color: cfg.tagColor,
      background: cfg.bg,
      border: `1px solid ${cfg.border}`,
      borderRadius: 20, padding: "2px 10px",
    }}>
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

/* ─── main ───────────────────────────────────────────────────── */
const SurveyResponsesList = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [data, setData]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 });

  const [searchText, setSearchText]       = useState("");
  const [selectedDates, setSelectedDates] = useState([]);
  const [giftStatus, setGiftStatus]       = useState(null);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentResponse, setCurrentResponse] = useState(null);
  const [isExporting, setIsExporting]         = useState(false);

  const filtersApplied = searchText.trim() !== "" || selectedDates.length > 0 || giftStatus !== null;

  /* ── API ── */
  const loadData = useCallback(
    async ({ page = pagination.current, pageSize = pagination.pageSize } = {}) => {
      if (!id) return;
      try {
        setLoading(true);
        const params = {
          page,
          per_page: pageSize,
          ...(searchText.trim() && { search: searchText.trim() }),
          ...(selectedDates.length === 2 && { from_date: selectedDates[0], to_date: selectedDates[1] }),
          ...(giftStatus && { gift_status: giftStatus }),
        };
        const response = await rewardAPIWithErrorHandling.getSubmissionList(id, params);
        const rd = response.data;
        if (rd.success) {
          setData(rd?.data?.data || []);
          setPagination({
            current:  rd?.data?.current_page ?? page,
            pageSize: rd?.data?.per_page     ?? pageSize,
            total:    rd?.data?.total        ?? 0,
          });
        }
      } catch (err) {
        message.error(err.message || "Có lỗi xảy ra khi tải dữ liệu");
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchText, selectedDates, giftStatus]
  );

  useEffect(() => { loadData({ page: 1 }); }, []); // eslint-disable-line

  const handleTableChange   = (pag) => loadData({ page: pag.current, pageSize: pag.pageSize });
  const handleSearchChange  = (e)   => setSearchText(e.target.value);
  const handleSearch        = ()    => loadData({ page: 1 });
  const handleDateChange    = (_, strings) => setSelectedDates(strings.every(Boolean) ? strings : []);
  const handleGiftStatusChange = (val) => setGiftStatus(val ?? null);

  const clearAllFilters = () => {
    setSearchText(""); setSelectedDates([]); setGiftStatus(null);
    form.resetFields(["rangePicker", "giftStatus"]);
    setTimeout(() => loadData({ page: 1 }), 0);
  };

  const handleViewResponseDetail = (record) => { setCurrentResponse(record); setIsModalVisible(true); };
  const handleCloseModal          = ()       => { setIsModalVisible(false); setCurrentResponse(null); };

  const handleProcessAction = async (record) => {
    try {
      await rewardAPIWithErrorHandling.sendRewardWithId(record.id);
      message.success("Xử lý thành công");
      loadData({ page: pagination.current });
    } catch (err) {
      message.error(err.message || "Xử lý thất bại");
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsExporting(true);
      const params = {
        ...(searchText.trim()          && { search:      searchText.trim() }),
        ...(selectedDates.length === 2 && { from_date: selectedDates[0], to_date: selectedDates[1] }),
        ...(giftStatus                 && { gift_status: giftStatus }),
        export: 1,
      };
      const response = await rewardAPIWithErrorHandling.exportSubmissionList(params);
      const url  = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `survey_responses_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success("Xuất Excel thành công");
    } catch (err) {
      message.error(err.message || "Xuất Excel thất bại");
    } finally {
      setIsExporting(false);
    }
  };

  /* ── render helpers ── */
  const renderResponseContent = (content) => {
    if (!content) return <Empty description="Không có dữ liệu" />;
    try {
      const obj = JSON.parse(content);
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {Object.entries(obj).map(([key, value], i) => (
            <div key={i} style={{
              display: "flex", gap: 12,
              padding: "8px 12px",
              background: i % 2 === 0 ? "#fafbfd" : "#fff",
              borderRadius: 6, border: "1px solid #f0f0f0",
            }}>
              <Text strong style={{ minWidth: 140, color: "#555", flexShrink: 0 }}>{key}</Text>
              <Text>{String(value) || "—"}</Text>
            </div>
          ))}
        </div>
      );
    } catch {
      return <Paragraph>{content}</Paragraph>;
    }
  };

  /* ── columns ── */
  const columns = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: 64,
      align: "center",
      render: (val) => <Text type="secondary" style={{ fontSize: 12 }}>{val}</Text>,
    },
    {
      title: "Người trả lời",
      key: "respondent",
      width: 220,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: brand.primary, marginBottom: 2 }}>
            {record.customer_name || <Text type="secondary">Ẩn danh</Text>}
          </div>
          {record.customer_phone && (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <PhoneOutlined style={{ fontSize: 11, color: "#aaa" }} />
              <Text type="secondary" style={{ fontSize: 12 }}>{record.customer_phone}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Thời gian",
      dataIndex: "created_at",
      key: "created_at",
      width: 140,
      align: "center",
      render: (val) => (
        <div style={{ lineHeight: 1.5 }}>
          <div style={{ fontSize: 12, color: brand.primary }}>{dayjs(val).format("DD/MM/YYYY")}</div>
          <div style={{ fontSize: 11, color: "#aaa" }}>{dayjs(val).format("HH:mm")}</div>
        </div>
      ),
    },
    {
      title: "Trạng thái",
      dataIndex: "gift_status",
      key: "gift_status",
      width: 140,
      align: "center",
      render: (status) => <StatusPill status={status} />,
    },
    {
      title: "Chi tiết hệ thống",
      dataIndex: "data_status",
      key: "data_status",
      width: 200,
      ellipsis: true,
      render: (_, record) =>
        record.data_status
          ? <Text type="secondary" style={{ fontSize: 12 }}>{record.data_status}</Text>
          : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>,
    },
    {
      title: "Thao tác",
      key: "action",
      align: "center",
      fixed: "right",
      width: 130,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Xem chi tiết">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewResponseDetail(record)}
            />
          </Tooltip>

          {record.gift_status === "pending" && (
            <Tooltip title="Bắt đầu xử lý">
              <Button
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => handleProcessAction(record)}
                style={{ color: brand.accent, borderColor: brand.accent }}
              />
            </Tooltip>
          )}
          {record.gift_status === "queued" && (
            <Tooltip title="Đang xử lý">
              <Button size="small" icon={<HourglassOutlined />} disabled />
            </Tooltip>
          )}
          {record.gift_status === "sent" && (
            <Tooltip title="Hoàn thành">
              <Button size="small" icon={<CheckCircleOutlined />} style={{ color: semantic.success, borderColor: semantic.success }} disabled />
            </Tooltip>
          )}
          {record.gift_status === "failed" && (
            <Tooltip title="Gửi lại">
              <Button
                size="small"
                danger
                icon={<RedoOutlined />}
                onClick={() => handleProcessAction(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  /* ── render ── */
  return (
    <div style={{ padding: "0 0 32px", background: "#f4f6fb", minHeight: "100vh" }}>

      {/* ── Page Header ── */}
      <div style={{
        background: "#fff",
        borderBottom: "1px solid #eaeef5",
        padding: "14px 24px 12px",
      }}>
        <Breadcrumb
          separator="›"
          style={{ marginBottom: 10, fontSize: 12 }}
          items={[
            { href: "/", title: <><HomeOutlined /> Trang chủ</> },
            { href: "/survey-responses", title: "Khảo sát & Quà tặng" },
            { title: "Danh sách phiếu trả lời" },
          ]}
        />

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              size="small"
              onClick={() => navigate(-1)}
              style={{ borderRadius: 6 }}
            >
              Quay lại
            </Button>
            <Divider type="vertical" style={{ margin: 0 }} />
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: brand.primary, lineHeight: 1.3 }}>
                Danh sách phiếu trả lời khảo sát
              </h2>
              <p style={{ margin: "2px 0 0", fontSize: 13, color: neutral[500] }}>
                Theo dõi và xử lý các phiếu phản hồi từ khách hàng
              </p>
            </div>
          </div>

          <Space size={8}>
            <div style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "6px 16px",
              background: "#f4f6fb",
              border: "1px solid #eaeef5",
              borderRadius: 8,
            }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#999" }}>Tổng</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: brand.accent, lineHeight: 1.2 }}>{pagination.total}</div>
              </div>
              <Divider type="vertical" style={{ margin: 0, height: 30 }} />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#999" }}>Hoàn thành</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: semantic.success, lineHeight: 1.2 }}>
                  {data.filter(d => d.gift_status === "sent").length}
                </div>
              </div>
              <Divider type="vertical" style={{ margin: 0, height: 30 }} />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#999" }}>Chờ xử lý</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#d46b08", lineHeight: 1.2 }}>
                  {data.filter(d => d.gift_status === "pending").length}
                </div>
              </div>
              <Divider type="vertical" style={{ margin: 0, height: 30 }} />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#999" }}>Thất bại</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#cf1322", lineHeight: 1.2 }}>
                  {data.filter(d => d.gift_status === "failed").length}
                </div>
              </div>
            </div>

            {/* Màn này gọi gateway riêng (rewardAPI) nên giữ handler cũ, chỉ mượn vỏ
                "Nhập / Xuất" dùng chung cho đồng bộ với các màn danh sách khác. */}
            <ImportExportMenu
              onExport={handleExportExcel}
              exporting={isExporting}
              disabled={data.length === 0}
              style={{ borderRadius: 6 }}
            />
          </Space>
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ padding: "16px 24px 0" }}>

        {/* Filter bar */}
        <Card
          style={{ borderRadius: 10, marginBottom: 12, border: "1px solid #eaeef5" }}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <Form form={form}>
            <Row gutter={10} align="middle" wrap>
              <Col flex="1 1 220px">
                <Input
                  prefix={<SearchOutlined style={{ color: "#c0c4cc" }} />}
                  placeholder="Tìm theo tên / SĐT / email..."
                  value={searchText}
                  onChange={handleSearchChange}
                  onPressEnter={handleSearch}
                  allowClear
                  onClear={() => loadData({ page: 1 })}
                  style={{ borderRadius: 8 }}
                />
              </Col>
              <Col>
                <Form.Item name="rangePicker" noStyle>
                  <RangePicker
                    onChange={handleDateChange}
                    style={{ borderRadius: 8 }}
                    placeholder={["Từ ngày", "Đến ngày"]}
                  />
                </Form.Item>
              </Col>
              <Col>
                <Form.Item name="giftStatus" noStyle>
                  <Select
                    placeholder="Trạng thái"
                    style={{ width: 160, borderRadius: 8 }}
                    onChange={handleGiftStatusChange}
                    allowClear
                    options={[
                      { value: "pending", label: "Chưa xử lý" },
                      { value: "queued",  label: "Đang xử lý" },
                      { value: "sent",    label: "Hoàn thành" },
                      { value: "failed",  label: "Thất bại" },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col>
                <Space size={6}>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={handleSearch}
                    style={{ borderRadius: 8 }}
                  >
                    Tìm kiếm
                  </Button>
                  <Tooltip title="Làm mới bộ lọc">
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={clearAllFilters}
                      style={{ borderRadius: 8 }}
                    />
                  </Tooltip>
                </Space>
              </Col>
            </Row>
          </Form>
        </Card>

        {/* Table */}
        <Card
          style={{ borderRadius: 10, border: "1px solid #eaeef5", overflow: "hidden" }}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            onChange={handleTableChange}
            pagination={{
              current:  pagination.current,
              pageSize: pagination.pageSize,
              total:    pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ["10", "20", "50"],
              showTotal: (total, range) => `${range[0]}–${range[1]} / ${total} bản ghi`,
              style: { padding: "12px 16px", margin: 0 },
            }}
            locale={{ emptyText: <Empty description="Không có phiếu trả lời khảo sát" /> }}
            scroll={{ x: "max-content", y: "calc(100vh - 420px)" }}
            size="small"
            rowClassName={(_, i) => i % 2 === 0 ? "srl-row-even" : "srl-row-odd"}
          />
        </Card>
      </div>

      {/* ── Detail Modal ── */}
      <Modal
        title={
          <Space>
            <FileTextOutlined style={{ color: brand.accent }} />
            <span style={{ fontWeight: 600 }}>Chi tiết phiếu trả lời</span>
            {currentResponse && (
              <StatusPill status={currentResponse.gift_status} />
            )}
          </Space>
        }
        open={isModalVisible}
        onCancel={handleCloseModal}
        width={720}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 12, color: "#aaa" }}>
              {currentResponse && `ID: #${currentResponse.id}`}
            </div>
            <Space>
              {currentResponse?.gift_status === "pending" && (
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={() => { handleProcessAction(currentResponse); handleCloseModal(); }}
                >
                  Bắt đầu xử lý
                </Button>
              )}
              {currentResponse?.gift_status === "failed" && (
                <Button
                  danger
                  icon={<RedoOutlined />}
                  onClick={() => { handleProcessAction(currentResponse); handleCloseModal(); }}
                >
                  Gửi lại
                </Button>
              )}
              <Button onClick={handleCloseModal}>Đóng</Button>
            </Space>
          </div>
        }
        styles={{
          body: { padding: "16px", maxHeight: "calc(100vh - 220px)", overflow: "auto" },
        }}
        destroyOnClose
      >
        {currentResponse && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

            {/* Info card */}
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 10, padding: "14px 16px",
              background: "#f8faff",
              border: "1px solid #dbeafe",
              borderRadius: 10,
            }}>
              {[
                { icon: <UserOutlined />,     label: "Họ tên",        value: currentResponse.customer_name  || "—" },
                { icon: <PhoneOutlined />,    label: "Số điện thoại", value: currentResponse.customer_phone || "—" },
                { icon: <CalendarOutlined />, label: "Thời gian",     value: formatDateTimeCustomize(currentResponse.created_at) },
                { icon: <InfoCircleOutlined />,label: "Trạng thái",   value: <StatusPill status={currentResponse.gift_status} /> },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <span style={{ color: brand.accent, fontSize: 14, marginTop: 1 }}>{item.icon}</span>
                  <div>
                    <div style={{ fontSize: 11, color: "#999" }}>{item.label}</div>
                    <div style={{ fontSize: 13, color: brand.primary, fontWeight: 500 }}>{item.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Answers */}
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: brand.primary, marginBottom: 8 }}>
                📋 Nội dung phiếu trả lời
              </div>
              {renderResponseContent(currentResponse.answers)}
            </div>

            {/* Timeline */}
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: brand.primary, marginBottom: 10 }}>
                🕐 Lịch sử hoạt động
              </div>
              <Timeline
                items={[
                  {
                    color: "green",
                    dot: <CheckCircleOutlined />,
                    children: (
                      <>
                        <Text strong style={{ fontSize: 12 }}>Tạo phiếu khảo sát</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {formatDateTime(currentResponse.created_at)}
                        </Text>
                      </>
                    ),
                  },
                  ...(currentResponse.schedule_time && currentResponse.gift_status === "sent"
                    ? [{
                        color: "blue",
                        dot: <SendOutlined />,
                        children: (
                          <>
                            <Text strong style={{ fontSize: 12 }}>Đã gửi quà</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {formatDateTime(currentResponse.schedule_time)}
                            </Text>
                          </>
                        ),
                      }]
                    : []),
                ]}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* scoped styles */}
      <style>{`
        .srl-row-even { background: #fff; }
        .srl-row-odd  { background: #fafbfd; }
        .srl-row-even td, .srl-row-odd td { vertical-align: middle !important; }
        .ant-table-tbody > tr:hover > td   { background: #eef5ff !important; transition: background 0.15s; }
        .ant-table-thead > tr > th {
          background: #f0f4fb !important;
          font-weight: 700 !important;
          font-size: 12px !important;
          color: #4a5568 !important;
          border-bottom: 1.5px solid #e2e8f0 !important;
          padding: 10px 12px !important;
        }
        .ant-table-cell { padding: 8px 12px !important; }
      `}</style>
    </div>
  );
};

export default SurveyResponsesList;